/**
 * pdfPageImages — plan + fetch the PDF page images embedded into the Excel/Word
 * export for PDF-region drugs (CA/EU/AU/UK and FDA letters), where the original
 * table structure is lost downstream so the faithful representation is the page
 * image itself.
 *
 * Two hard-won rules from the image-scale benchmark (doc 5000, 145 pages):
 *  - Embed only the pages that carry a comparison hit, not the whole document.
 *    Full-document embedding fails on fetch count, file size AND writeBuffer
 *    freeze at once; matched-pages is 1-2 orders of magnitude smaller.
 *  - The workload budget is embedded pages/bytes, NOT drug count. 100 text-only
 *    drugs cost ~0; 10 PDF drugs × 145 pages is gigabytes.
 *
 * Quality is adaptive but deterministic and auditable: prefer Standard, step to
 * Reduced then Minimum only to fit the budget, never silently below Minimum —
 * over the hard ceiling we reject with a clear message rather than degrade
 * regulatory pages into unreadability.
 */
import type { ExportDrug, ExportDrugPdf } from './compareExport';
import type { ExportImageFormat } from './exportImageLayout';
import { stagePercent, throwIfAborted, type ExportRunOptions } from './exportProgress';
import { marksByPage, paintMarks, type PageMark } from './pageMarks';

export interface QualityTier {
  /** Shown in the workbook audit note. */
  name: 'Standard' | 'Reduced' | 'Minimum';
  /** JPEG quality 0..1 for canvas re-encode. */
  quality: number;
  /** Longest-edge cap in px. */
  maxDim: number;
  /** Measured average KB/page for this tier on regulatory pages (benchmark). */
  kbPerPage: number;
}

// Measured on real regulatory pages (see xlsxbench): as-is PNG ≈222 KB/page;
// JPEG q40 remains a *candidate* floor — not approved until the Japanese
// small-kanji visual check, so callers should not default below Reduced.
export const QUALITY_TIERS: readonly QualityTier[] = [
  { name: 'Standard', quality: 0.6, maxDim: 900, kbPerPage: 102 },
  { name: 'Reduced', quality: 0.5, maxDim: 800, kbPerPage: 71 },
  { name: 'Minimum', quality: 0.4, maxDim: 700, kbPerPage: 50 },
];

export const EXPORT_IMAGE_LIMITS = {
  /** Hard cap on unique embedded pages regardless of bytes (fetch-count guard). */
  maxUniquePdfPages: 1500,
  /** Step quality down to try to keep estimated media under this. */
  preferredMediaBytes: 120 * 1024 * 1024,
  /** Absolute ceiling; over this at the lowest tier we reject the export. */
  maxEstimatedMediaBytes: 250 * 1024 * 1024,
};

/** Scales the byte budget per format. HTML embeds pages as base64 data URIs,
 *  which cost 4/3 of the raw bytes, so its budget shrinks to ~3/4 and the
 *  quality ladder steps down (or refuses) earlier — instead of emitting a file
 *  a third larger than the estimate said. */
export const FORMAT_BYTE_FACTOR: Readonly<Record<ExportImageFormat, number>> = {
  xlsx: 1.0,
  docx: 1.0,
  html: 0.73,
};

const FORMAT_LABEL: Readonly<Record<ExportImageFormat, string>> = {
  xlsx: 'Excel',
  docx: 'Word',
  html: 'HTML',
};

export interface PlannedPage {
  pageNum: number;
  imageUrl: string;
  width: number | null;
  height: number | null;
  /** The keyword boxes on this page, painted into the image (pageMarks.ts). */
  marks?: PageMark[];
}

/** The source pages to embed for a PDF drug: the COMPLETE document (every page),
 *  deduped by page number. Full-document embedding is intentional for now — the
 *  page/byte guard below still protects the browser from an over-large export by
 *  stepping image quality down and, past the hard ceiling, refusing rather than
 *  crashing. (Matched-pages-only would be the lever if we later need scale.) */
export function pagesToEmbed(pdf: ExportDrugPdf): PlannedPage[] {
  if (pdf.sectionScoped) return [];
  const seen = new Set<number>();
  const out: PlannedPage[] = [];
  const marks = marksByPage(pdf.marks);
  for (const p of pdf.pages) {
    if (seen.has(p.page_num)) continue;
    seen.add(p.page_num);
    const pageMarks = marks.get(p.page_num);
    out.push({
      pageNum: p.page_num, imageUrl: p.image_url, width: p.width, height: p.height,
      ...(pageMarks ? { marks: pageMarks } : {}),
    });
  }
  return out;
}

export interface ImagePlan {
  /** Per drug (aligned to the input array), the pages to embed. Empty for non-PDF. */
  perDrug: PlannedPage[][];
  totalPages: number;
  tier: QualityTier | null; // null ⇒ rejected
  estimatedBytes: number;
  rejected: boolean;
  reason?: string;
}

/** Choose the highest quality tier whose estimate fits the preferred budget,
 *  falling back toward Minimum, and reject past the hard ceiling / page cap.
 *  The byte budget depends on the format (FORMAT_BYTE_FACTOR); the page cap
 *  does not. `estimatedBytes` is always the raw image bytes. */
export function planExportImages(drugs: ExportDrug[], format: ExportImageFormat = 'xlsx'): ImagePlan {
  const perDrug = drugs.map((d) => (d.pdf ? pagesToEmbed(d.pdf) : []));
  const totalPages = perDrug.reduce((n, pages) => n + pages.length, 0);

  if (totalPages === 0) {
    return { perDrug, totalPages, tier: null, estimatedBytes: 0, rejected: false };
  }
  if (totalPages > EXPORT_IMAGE_LIMITS.maxUniquePdfPages) {
    return {
      perDrug, totalPages, tier: null, estimatedBytes: 0, rejected: true,
      reason: `This comparison would embed ${totalPages.toLocaleString()} PDF pages — over the ${EXPORT_IMAGE_LIMITS.maxUniquePdfPages.toLocaleString()}-page ${FORMAT_LABEL[format]} limit. Narrow the search or export fewer drugs.`,
    };
  }
  const factor = FORMAT_BYTE_FACTOR[format];
  const est = (tier: QualityTier) => totalPages * tier.kbPerPage * 1024;
  const withinPreferred = QUALITY_TIERS.find((t) => est(t) <= EXPORT_IMAGE_LIMITS.preferredMediaBytes * factor);
  const tier = withinPreferred ?? QUALITY_TIERS[QUALITY_TIERS.length - 1]!;
  const estimatedBytes = est(tier);
  if (estimatedBytes > EXPORT_IMAGE_LIMITS.maxEstimatedMediaBytes * factor) {
    return {
      perDrug, totalPages, tier: null, estimatedBytes, rejected: true,
      reason: `This comparison has too many PDF pages to embed (~${Math.round(estimatedBytes / 1024 / 1024)} MB even at the smallest image size). Narrow the search or export fewer drugs.`,
    };
  }
  return { perDrug, totalPages, tier, estimatedBytes, rejected: false };
}

export interface EmbeddedPage {
  buffer: ArrayBuffer;
  extension: 'jpeg' | 'png';
  width: number;
  height: number;
  pageNum: number;
}

/** A small pool of Web Workers that fetch + downsample page images off the main
 *  thread, so a large export doesn't freeze the tab. Falls back to the inline
 *  path (below) when Workers/OffscreenCanvas are unavailable. */
class WorkerEncoderPool {
  private workers: Worker[] = [];
  private idle: Worker[] = [];
  private waiting: ((w: Worker) => void)[] = [];
  private seq = 0;

  constructor(size: number) {
    for (let i = 0; i < size; i++) {
      const w = new Worker(new URL('./pdfImageWorker.ts', import.meta.url), { type: 'module' });
      this.workers.push(w);
      this.idle.push(w);
    }
  }
  private acquire(): Promise<Worker> {
    const w = this.idle.pop();
    return w ? Promise.resolve(w) : new Promise((res) => this.waiting.push(res));
  }
  private release(w: Worker): void {
    const next = this.waiting.shift();
    if (next) next(w); else this.idle.push(w);
  }
  encode(page: PlannedPage, tier: QualityTier): Promise<EmbeddedPage | null> {
    return this.acquire().then((w) => new Promise<EmbeddedPage | null>((resolve) => {
      const id = ++this.seq;
      const onMsg = (e: MessageEvent) => {
        if ((e.data as { id?: number })?.id !== id) return;
        w.removeEventListener('message', onMsg);
        this.release(w);
        const d = e.data as { ok: boolean; buffer?: ArrayBuffer; width?: number; height?: number };
        resolve(d.ok && d.buffer ? { buffer: d.buffer, extension: 'jpeg', width: d.width ?? 0, height: d.height ?? 0, pageNum: page.pageNum } : null);
      };
      w.addEventListener('message', onMsg);
      w.postMessage({ id, url: page.imageUrl, quality: tier.quality, maxDim: tier.maxDim, marks: page.marks ?? [] });
    }));
  }
  get size(): number { return this.workers.length; }
  dispose(): void { this.workers.forEach((w) => w.terminate()); }
}

/** Fetches one page and shrinks it to the tier. The browser uses its Web
 *  Worker pool or canvas; the server export worker installs a sharp-based
 *  encoder with setPageEncoder — the rest of the pipeline is shared. */
export type PageEncoder = (page: PlannedPage, tier: QualityTier) => Promise<EmbeddedPage | null>;

let pageEncoder: PageEncoder | null = null;

export function setPageEncoder(encoder: PageEncoder | null): void {
  pageEncoder = encoder;
}

function canUseWorkers(): boolean {
  return typeof Worker !== 'undefined' && typeof OffscreenCanvas !== 'undefined' && typeof createImageBitmap === 'function';
}

/** Fetch one page image and re-encode it to JPEG at the tier's quality/size via
 *  canvas — the main-thread fallback when Workers aren't available. In a
 *  non-browser/canvas-less environment the raw bytes are returned unchanged. */
async function fetchAndEncode(page: PlannedPage, tier: QualityTier): Promise<EmbeddedPage | null> {
  try {
    const resp = await fetch(page.imageUrl);
    if (!resp.ok) return null;
    const blob = await resp.blob();
    const canDownsample = typeof document !== 'undefined' && typeof createImageBitmap === 'function';
    if (!canDownsample) {
      const buffer = await blob.arrayBuffer();
      return { buffer, extension: blob.type.includes('png') ? 'png' : 'jpeg', width: page.width ?? 0, height: page.height ?? 0, pageNum: page.pageNum };
    }
    const bmp = await createImageBitmap(blob);
    const scale = Math.min(1, tier.maxDim / Math.max(bmp.width, bmp.height));
    const w = Math.max(1, Math.round(bmp.width * scale));
    const h = Math.max(1, Math.round(bmp.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) { const buffer = await blob.arrayBuffer(); return { buffer, extension: 'jpeg', width: w, height: h, pageNum: page.pageNum }; }
    ctx.drawImage(bmp, 0, 0, w, h);
    bmp.close?.();
    paintMarks(ctx, page.marks, w, h);
    const outBlob: Blob | null = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', tier.quality));
    if (!outBlob) return null;
    const buffer = await outBlob.arrayBuffer();
    return { buffer, extension: 'jpeg', width: w, height: h, pageNum: page.pageNum };
  } catch {
    return null; // a single unreachable page must not fail the whole export
  }
}

/** Fetch + encode all planned pages per drug, bounded to `concurrency` in flight.
 *  Aborting `signal` terminates the encoder workers at once and rejects with an
 *  AbortError — pages already in flight are abandoned, not awaited. */
export async function fetchEmbeddedPages(
  plan: ImagePlan,
  concurrency = 10,
  onProgress?: (done: number, total: number) => void,
  signal?: AbortSignal,
): Promise<(EmbeddedPage[] | undefined)[]> {
  throwIfAborted(signal);
  if (plan.rejected || !plan.tier) return plan.perDrug.map(() => undefined);
  const tier = plan.tier;
  const jobs: { drug: number; page: PlannedPage }[] = [];
  plan.perDrug.forEach((pages, drug) => pages.forEach((page) => jobs.push({ drug, page })));

  // Prefer a worker pool (fetch + downsample off the main thread). If a page
  // fails in the worker, retry it inline so one bad page never loses content.
  let pool: WorkerEncoderPool | null = null;
  if (!pageEncoder && canUseWorkers()) {
    try {
      const cores = typeof navigator !== 'undefined' ? navigator.hardwareConcurrency || 4 : 4;
      pool = new WorkerEncoderPool(Math.max(2, Math.min(concurrency, cores)));
    } catch { pool = null; }
  }
  const inFlight = pool ? pool.size : concurrency;
  const custom = pageEncoder;
  const encodeOne = async (page: PlannedPage): Promise<EmbeddedPage | null> => {
    if (custom) return custom(page, tier).catch(() => null); // one bad page never fails the export
    return pool ? (await pool.encode(page, tier)) ?? (await fetchAndEncode(page, tier)) : fetchAndEncode(page, tier);
  };

  const out: (EmbeddedPage[] | undefined)[] = plan.perDrug.map((pages) => (pages.length ? [] : undefined));
  let next = 0, done = 0;
  async function runner() {
    while (next < jobs.length && !signal?.aborted) {
      const { drug, page } = jobs[next++]!;
      const enc = await encodeOne(page);
      if (signal?.aborted) return;
      if (enc) out[drug]!.push(enc);
      onProgress?.(++done, jobs.length);
    }
  }
  // A terminated worker never answers, so its encode() never settles: race the
  // runners against the abort instead of waiting for them.
  let onAbort: (() => void) | undefined;
  const aborted = new Promise<never>((_, reject) => {
    onAbort = () => { pool?.dispose(); reject(new DOMException('Export cancelled', 'AbortError')); };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
  aborted.catch(() => {}); // the race below observes it; this only silences the unobserved case
  try {
    await Promise.race([Promise.all(Array.from({ length: Math.min(inFlight, jobs.length) }, runner)), aborted]);
  } finally {
    if (onAbort) signal?.removeEventListener('abort', onAbort);
  }
  pool?.dispose();
  // keep page order stable within each drug
  out.forEach((pages) => pages?.sort((a, b) => a.pageNum - b.pageNum));
  return out;
}

export interface ExportImages {
  /** Per drug (aligned to the input array); undefined for a drug with no pages
   *  and for every drug when the plan was rejected. */
  images: (EmbeddedPage[] | undefined)[] | undefined;
  /** Pages actually embedded (failed fetches are not counted). */
  embedded: number;
  /** Total image bytes embedded — reported to the timings telemetry. */
  mediaBytes: number;
  /** The audit line every format prints in its header, or undefined when
   *  there was nothing to embed. */
  note: string | undefined;
  plan: ImagePlan;
}

/** Plan → fetch → audit note, once, for every format. A rejected plan still
 *  returns (with a note explaining why the images were omitted) so the caller
 *  exports the text rather than failing. */
export async function embedPagesForExport(
  drugs: ExportDrug[],
  format: ExportImageFormat,
  onProgress?: (done: number, total: number) => void,
  signal?: AbortSignal,
): Promise<ExportImages> {
  const plan = planExportImages(drugs, format);
  const images = plan.rejected ? undefined : await fetchEmbeddedPages(plan, undefined, onProgress, signal);
  const embedded = images?.reduce((n, pages) => n + (pages?.length ?? 0), 0) ?? 0;
  const mediaBytes = images?.reduce((n, pages) => n + (pages ?? []).reduce((m, p) => m + p.buffer.byteLength, 0), 0) ?? 0;
  const note = plan.rejected
    ? `PDF pages not embedded — ${plan.reason}`
    : embedded > 0
      ? `${embedded} PDF page image(s) embedded · quality: ${plan.tier?.name ?? 'Standard'}`
      : undefined;
  return { images, embedded, mediaBytes, note, plan };
}

/** The images stage of a downloader, on the shared progress scale: times it,
 *  reports 15→75%, honours cancel, then enters the build band. */
export async function embedPagesStage(
  drugs: ExportDrug[],
  format: ExportImageFormat,
  options: ExportRunOptions,
): Promise<ExportImages> {
  const { onProgress, signal, timer } = options;
  throwIfAborted(signal);
  timer?.start('images');
  onProgress?.(stagePercent('images'));
  const out = await embedPagesForExport(drugs, format, (done, total) => onProgress?.(stagePercent('images', done, total)), signal);
  throwIfAborted(signal);
  timer?.start('build');
  onProgress?.(stagePercent('build'));
  return out;
}
